<template>
  <div class="pm">
    <!-- Il totale si vede appena la modale si apre: e' il numero per cui si
         sta aprendo. Le righe della variazione e il nuovo totale si
         aggiungono qui sotto mentre si scrive, con le stesse regole
         dell'API. -->
    <div class="pm-conto">
      <div class="pm-riga">
        <span>Totale del documento</span>
        <span>{{ soldi(totaleAdesso) }}</span>
      </div>
      <template v-if="conto">
        <div class="pm-riga">
          <span>{{ tipo === "sconto" ? "Sconto" : "Maggiorazione" }}</span>
          <span>
            {{ tipo === "sconto" ? "−" : "+" }} {{ soldi(conto.importo) }}
          </span>
        </div>
        <div class="pm-riga pm-riga--totale">
          <span>Nuovo totale</span>
          <span>{{ soldi(conto.dopo) }}</span>
        </div>
      </template>
    </div>

    <!-- Quello che c'è già. Si guarda l'importo e non la percentuale: a valore
         fisso la percentuale è zero, e la riga sparirebbe proprio quando c'è
         uno sconto in euro. -->
    <div v-if="haModificatori" class="pm-attuali">
      <div v-if="sconto > 0" class="pm-attuale">
        <p>
          <strong>Sconto applicato:</strong>
          − {{ soldi(sconto) }}
          <span v-if="scontoPercento > 0" class="pm-attuale__quanto">
            ({{ scontoPercento }}%)
          </span>
        </p>
        <button
          type="button"
          class="pm-togli"
          :disabled="readOnly || inCorso"
          @click="togli('sconto')"
        >
          <i class="fa-solid fa-trash-can"></i> Togli
        </button>
      </div>

      <div v-if="maggiorazione > 0" class="pm-attuale">
        <p>
          <strong>Maggiorazione applicata:</strong>
          + {{ soldi(maggiorazione) }}
          <span v-if="maggiorazionePercento > 0" class="pm-attuale__quanto">
            ({{ maggiorazionePercento }}%)
          </span>
        </p>
        <button
          type="button"
          class="pm-togli"
          :disabled="readOnly || inCorso"
          @click="togli('maggiorazione')"
        >
          <i class="fa-solid fa-trash-can"></i> Togli
        </button>
      </div>

      <p class="pm-nota">
        Sconto e maggiorazione sono alternativi: per cambiarli, togli prima
        quello che c'è.
      </p>
    </div>

    <p class="pm-titolo">Cambia il prezzo di questo documento</p>

    <div class="pm-scelte">
      <button
        type="button"
        class="pm-scelta"
        :class="{ 'pm-scelta--attiva': tipo === 'sconto' }"
        :disabled="readOnly || maggiorazione > 0"
        @click="scegli('sconto')"
      >
        <i class="fa-solid fa-tag"></i>
        <span class="pm-scelta__nome">Sconto</span>
        <span class="pm-scelta__desc">Il totale scende</span>
      </button>
      <button
        type="button"
        class="pm-scelta"
        :class="{ 'pm-scelta--attiva': tipo === 'maggiorazione' }"
        :disabled="readOnly || sconto > 0"
        @click="scegli('maggiorazione')"
      >
        <i class="fa-solid fa-arrow-trend-up"></i>
        <span class="pm-scelta__nome">Maggiorazione</span>
        <span class="pm-scelta__desc">Il totale sale</span>
      </button>
    </div>

    <template v-if="tipo">
      <div class="pm-campi">
        <div class="pm-campo">
          <span>Come</span>
          <StatusSelect
            v-model="modo"
            :options="OPZIONI_MODO"
            placeholderIcon="fa-percent"
          />
        </div>

        <label class="pm-campo">
          <span>{{ modo === "percentuale" ? "Percentuale" : "Importo" }}</span>
          <div class="pm-valore">
            <span v-if="modo === 'valore'" class="pm-unita">€</span>
            <!-- Il valore non passa da `v-model`: `max` sul campo numerico
                 non impedisce di scrivere, avverte e basta. Qui si taglia
                 mentre si digita, cosi' il numero fuori limite non compare
                 proprio. -->
            <input
              :value="valore"
              type="number"
              min="0"
              :max="tetto"
              step="0.01"
              @input="scriviValore($event)"
            />
            <span v-if="modo === 'percentuale'" class="pm-unita">%</span>
          </div>
        </label>

        <label class="pm-campo pm-campo--largo">
          <span>Perché (facoltativo)</span>
          <input
            v-model.trim="motivazione"
            type="text"
            maxlength="255"
            placeholder="Resta nel log attività"
          />
        </label>
      </div>

      <p v-if="!conto && cosaManca" class="pm-nota">{{ cosaManca }}</p>

      <div class="pm-azioni">
        <button
          type="button"
          class="primary-btn"
          :disabled="readOnly || inCorso || !conto"
          @click="applica"
        >
          {{ occupato === "applica" ? "Applico…" : "Applica" }}
        </button>
      </div>
    </template>

    <p v-if="errore" class="pm-errore">{{ errore }}</p>
  </div>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import StatusSelect from "@/components/StatusSelect.vue";
import { formatPriceToPrint } from "@/utilities/formatting";
import { packagingFeeOf } from "@/utilities/packagingFee";

/**
 * [sc-48351 / task 48783] Sconto o maggiorazione su un documento.
 *
 * Sta in un componente e non dentro le pagine perché serve identico in due
 * posti — Preventivi e Ordini — e due copie dello stesso conto sarebbero due
 * copie da tenere allineate.
 *
 * **I soldi li calcola l'API.** Qui si manda quanto e come; il conto che si
 * vede prima di premere usa le stesse regole, ma è un'anteprima, non la
 * verità: se i due numeri divergessero, quello giusto è il suo.
 *
 * **L'acconto non c'entra.** Scriveva `quotes.deposit`, che è la cache del
 * totale pagato: gli acconti si registrano dai pagamenti dell'ordine (48692).
 */
export default {
  name: "PriceModifierModal",
  components: { StatusSelect },
  props: {
    quote: { type: Object, required: true },
    readOnly: { type: Boolean, default: false },
  },
  emits: ["applied"],
  data() {
    return {
      tipo: "",
      modo: "percentuale",
      OPZIONI_MODO: [
        { value: "percentuale", label: "In percentuale" },
        { value: "valore", label: "A valore fisso" },
      ],
      valore: "",
      motivazione: "",
      occupato: "",
      errore: "",
    };
  },
  computed: {
    sconto() {
      return Number(this.quote?.discount) || 0;
    },
    scontoPercento() {
      return Number(this.quote?.discount_percent) || 0;
    },
    maggiorazione() {
      return Number(this.quote?.surcharge) || 0;
    },
    maggiorazionePercento() {
      return Number(this.quote?.surcharge_percent) || 0;
    },
    /**
     * `occupato` e' una stringa perche' dice *quale* pulsante sta lavorando.
     * Legata a `:disabled` da sola, il valore vuoto diventa `disabled=""`, e
     * Vue lo considera presente: i pulsanti nascevano spenti per sempre. Gia'
     * visto nella pagina delle variazioni di listino.
     */
    inCorso() {
      return Boolean(this.occupato);
    },
    haModificatori() {
      return this.sconto > 0 || this.maggiorazione > 0;
    },
    /**
     * Il totale che il cliente legge in fondo al documento. L'imballo con la
     * regola del server (`packagingFeeOf`): `packagingFee` non esiste.
     */
    totaleAdesso() {
      return this.arrotonda(
        (Number(this.quote?.totalPrice) || 0) -
          this.sconto +
          this.maggiorazione +
          packagingFeeOf(this.quote)
      );
    },
    /** Su cosa si calcola: il totale delle righe, come nell'API. */
    base() {
      return Number(this.quote?.totalPrice) || 0;
    },
    /**
     * Il massimo che si può scrivere nel campo. In percentuale è il 100%:
     * oltre, uno sconto porterebbe il totale sotto zero. A valore fisso è il
     * totale delle righe, che va superato per difetto — un valore pari al
     * totale è uno sconto del 100%, e quello si chiede in percentuale.
     */
    tetto() {
      return this.modo === "percentuale" ? 100 : this.base;
    },
    /** Il conto, o `null` se non c'è ancora niente da mostrare. */
    conto() {
      const valore = Number(this.valore);
      if (!this.tipo || !Number.isFinite(valore) || valore <= 0) return null;
      if (this.modo === "percentuale" && valore > 100) return null;
      const importo =
        this.modo === "percentuale"
          ? this.arrotonda(this.base * (valore / 100))
          : this.arrotonda(valore);
      if (importo <= 0) return null;
      if (this.modo === "valore" && importo >= this.base) return null;
      if (this.tipo === "sconto" && importo > this.base) return null;
      const segno = this.tipo === "sconto" ? -1 : 1;
      return {
        prima: this.totaleAdesso,
        importo,
        dopo: this.arrotonda(this.totaleAdesso + segno * importo),
      };
    },
    /** Perché «Applica» è ancora spento. Gli stessi limiti dell'API. */
    cosaManca() {
      const valore = Number(this.valore);
      if (!Number.isFinite(valore) || valore <= 0) {
        return this.modo === "percentuale"
          ? "Scrivi la percentuale."
          : "Scrivi l'importo.";
      }
      if (this.modo === "percentuale") {
        return valore > 100 ? "La percentuale non può superare il 100%." : "";
      }
      return `Il valore fisso deve restare sotto ${this.soldi(this.base)}.`;
    },
  },
  watch: {
    // Passando da valore fisso a percentuale (e viceversa) il tetto cambia:
    // un numero che stava dentro puo' trovarsi fuori.
    modo() {
      this.valore = this.dentroAlTetto(this.valore);
    },
  },
  methods: {
    soldi: formatPriceToPrint,
    /** Il numero riportato dentro `0` e `tetto`; stringa vuota se non c'è. */
    dentroAlTetto(grezzo) {
      const testo = String(grezzo ?? "").trim();
      if (testo === "") return "";
      const numero = Number(testo);
      if (!Number.isFinite(numero)) return "";
      if (numero < 0) return "0";
      if (numero > this.tetto) return String(this.tetto);
      // Via gli zeri davanti: «05» si scrive partendo da «0», ma quello che
      // resta nel campo dev'essere «5». Lo zero da solo, e «0,5», restano:
      // scriverli e' l'unico modo per arrivare ai decimali.
      return testo.replace(/^0+(?=\d)/, "");
    },
    /**
     * Scrive il valore tagliato al tetto. Il campo va riallineato a mano:
     * digitando `1000` su un valore già fermo a `100` il modello non cambia,
     * Vue non ridisegna, e nella casella resterebbe `1000`.
     */
    scriviValore(evento) {
      const campo = evento.target;
      this.valore = this.dentroAlTetto(campo.value);
      if (campo.value !== String(this.valore))
        campo.value = String(this.valore);
    },
    arrotonda(n) {
      return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    },
    scegli(tipo) {
      // Ripremendo la stessa scheda si torna indietro: è il modo più ovvio di
      // dire «no, lascia stare».
      this.tipo = this.tipo === tipo ? "" : tipo;
      this.valore = "";
      this.errore = "";
    },
    async applica() {
      if (!this.conto) return;
      this.occupato = "applica";
      this.errore = "";
      try {
        const { data } = await axiosInstance.post(
          `quote/priceAdjustment/${this.quote.id}`,
          {
            type: this.tipo,
            modo: this.modo,
            valore: Number(this.valore),
            motivazione: this.motivazione || undefined,
          }
        );
        this.$emit("applied", {
          tipo: this.tipo,
          modo: this.modo,
          valore: Number(this.valore),
          importo: data?.importo ?? this.conto.importo,
          totale: this.conto.dopo,
          messaggio: data?.message,
        });
        this.tipo = "";
        this.valore = "";
        this.motivazione = "";
      } catch (err) {
        this.errore = err?.response?.data?.message || "Modifica non applicata.";
      } finally {
        this.occupato = "";
      }
    },
    async togli(tipo) {
      this.occupato = "togli";
      this.errore = "";
      try {
        const { data } = await axiosInstance.delete(
          `quote/priceAdjustment/${this.quote.id}`,
          { data: { type: tipo } }
        );
        this.$emit("applied", {
          tipo,
          tolto: true,
          messaggio: data?.message,
        });
      } catch (err) {
        this.errore = err?.response?.data?.message || "Modifica non tolta.";
      } finally {
        this.occupato = "";
      }
    },
  },
};
</script>

<style scoped>
/* Gli spazi sono il motivo per cui questo pannello è stato rifatto: prima i
   campi si toccavano fra loro e la modale sembrava un elenco senza respiro. */
.pm {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 4px;
}

.pm-titolo {
  margin: 0;
  color: var(--color-text-muted, #555);
  font-size: 14px;
}

.pm-attuali {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 10px;
  background: var(--color-surface-alt, #f7f7f8);
}

.pm-attuale {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
}

.pm-attuale p {
  margin: 0;
  font-size: 14px;
}

.pm-attuale__quanto {
  color: var(--color-text-muted, #71717a);
}

.pm-togli {
  padding: 6px 12px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 8px;
  background: var(--color-surface, #fff);
  color: #b00020;
  font-size: 13px;
  cursor: pointer;
}

.pm-togli:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Le due schede: una accanto all'altra sopra i 520px, impilate sotto. */
.pm-scelte {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.pm-scelta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 18px 16px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 12px;
  background: var(--color-surface, #fff);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.pm-scelta i {
  font-size: 18px;
  color: var(--color-text-muted, #71717a);
}

.pm-scelta__nome {
  font-weight: 700;
  font-size: 14px;
}

.pm-scelta__desc {
  color: var(--color-text-muted, #71717a);
  font-size: 12px;
}

.pm-scelta--attiva {
  border-color: var(--color-primary, #005f86);
  background: var(--color-surface-alt, #f4f4f5);
}

.pm-scelta--attiva i {
  color: var(--color-primary, #005f86);
}

.pm-scelta:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pm-campi {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.pm-campo {
  display: flex;
  flex: 1 1 180px;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
}

.pm-campo--largo {
  flex-basis: 100%;
}

/* `AccountPage.css` non è scoped e impone `.field input { width: 350px }` a
   tutto il gestionale: qui i campi seguono la colonna. */
/* La tendina della piattaforma con la forma degli altri campi. */
.pm-campo :deep(.status-select) {
  display: block;
  width: 100%;
}
.pm-campo :deep(.status-select__trigger) {
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 14px;
}

.pm-campo input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 10px;
  font-size: 14px;
}

.pm-valore {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pm-unita {
  color: var(--color-text-muted, #71717a);
  font-weight: 600;
}

.pm-conto {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 10px;
  background: var(--color-surface-alt, #f7f7f8);
}

.pm-riga {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 14px;
}

/* Da sola, la prima riga e' il totale del documento: pesa un po' di piu'. */
.pm-conto .pm-riga:first-child span:last-child {
  font-weight: 600;
}

.pm-riga--totale {
  padding-top: 8px;
  border-top: 0.5px solid var(--color-border, #d4d4d8);
  font-weight: 700;
}

.pm-azioni {
  display: flex;
  justify-content: flex-end;
}

.pm-azioni button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pm-nota {
  margin: 0;
  color: var(--color-text-muted, #71717a);
  font-size: 13px;
}

.pm-errore {
  margin: 0;
  color: #b00020;
  font-size: 14px;
}
</style>
